// BlacklistTable.js
import React, { Component } from 'react';
import firebase from '../../store/firebase';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

class BlacklistTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      blacklistUsers: [],
      filteredUsers: [],
      loading: true,
      error: null,
      searchTerm: ''
    };
  }

  componentDidMount() {
    const blacklistRef = firebase.database.ref('blacklist');

    blacklistRef.on('value', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const users = Object.keys(data)
          .map(key => ({
            id: key,
            ...data[key]
          }))
          .sort((a, b) => 
            new Date(b.timestamp) - new Date(a.timestamp) // Newest first
          );

        this.setState({
          blacklistUsers: users,
          loading: false
        }, () => this.applyFilters());
      } else {
        this.setState({
          blacklistUsers: [],
          filteredUsers: [],
          loading: false
        });
      }
    }, (error) => {
      console.error("Firebase error loading blacklist:", error);
      this.setState({
        error: error.message,
        loading: false
      });
    });
  }

  componentWillUnmount() {
    firebase.database.ref('blacklist').off('value');
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value }, this.applyFilters);
  };

  applyFilters = () => {
    const { blacklistUsers, searchTerm } = this.state;
    const searchLower = searchTerm.trim().toLowerCase();

    const filtered = blacklistUsers.filter(user => {
      if (!searchLower) return true;

      return (
        (user.email || '').toLowerCase().includes(searchLower) ||
        (user.uid || '').toLowerCase().includes(searchLower) ||
        (user.userID || '').toLowerCase().includes(searchLower) ||
        (user.userName || '').toLowerCase().includes(searchLower) ||
        (user.attemptedCustodialWallet || '').toLowerCase().includes(searchLower) ||
        (user.reason || '').toLowerCase().includes(searchLower)
      );
    });

    this.setState({ filteredUsers: filtered });
  };

  formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // === EXCEL EXPORT FUNCTION ===
  downloadExcel = async () => {
    const { filteredUsers } = this.state;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Blacklist');

    worksheet.columns = [
      { header: 'Date Added', key: 'timestamp', width: 20 },
      { header: 'UID', key: 'uid', width: 35 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'User Name', key: 'userName', width: 20 },
      { header: 'User ID', key: 'userID', width: 15 },
      { header: 'Attempted Wallet', key: 'attemptedCustodialWallet', width: 45 },
      { header: 'Reason', key: 'reason', width: 60 }
    ];

    filteredUsers.forEach(user => {
      worksheet.addRow({
        timestamp: this.formatTimestamp(user.timestamp),
        uid: user.uid || '',
        email: user.email || '',
        userName: user.userName || '',
        userID: user.userID || '',
        attemptedCustodialWallet: user.attemptedCustodialWallet || user.custodialwallet || '',
        reason: user.reason || 'Unknown'
      });
    });

    // Style header
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFF9999' } // Light red background for warning
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Blacklist_Export.xlsx');
  };

  render() {
    const { filteredUsers, loading, error, searchTerm } = this.state;

    if (loading) return <div>Loading blacklist...</div>;
    if (error) return <div>Error loading blacklist: {error}</div>;

    return (
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <h2 style={{ color: '#d32f2f' }}>Blacklisted Users</h2>

        {/* Search + Download */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3f3', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Search:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={this.handleSearchChange}
                placeholder="Email, UID, wallet, reason..."
                style={inputStyle}
              />
            </div>

            <div style={{ marginLeft: 'auto' }}>
              <button onClick={this.downloadExcel} style={buttonStyle}>
                📊 Download Excel
              </button>
            </div>

            <div style={{ color: '#555', fontSize: '14px' }}>
              Total blacklisted: <strong>{filteredUsers.length}</strong>
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredUsers.length === 0 ? (
          <p>No users currently blacklisted.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#ffebee' }}>
                <th style={thStyle}>Date Added</th>
                <th style={thStyle}>UID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>User Name</th>
                <th style={thStyle}>User ID</th>
                <th style={thStyle}>Attempted Wallet</th>
                <th style={thStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #ddd', backgroundColor: '#fafafa' }}>
                  <td style={tdStyle}>{this.formatTimestamp(user.timestamp)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>
                    {user.uid || '-'}
                  </td>
                  <td style={tdStyle}>
                    {user.email ? (
                      <a href={`mailto:${user.email}`} style={{ color: '#d32f2f' }}>
                        {user.email}
                      </a>
                    ) : '-'}
                  </td>
                  <td style={tdStyle}>{user.userName || '-'}</td>
                  <td style={tdStyle}>{user.userID || '-'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                    {user.attemptedCustodialWallet || user.custodialwallet || '-'}
                  </td>
                  <td style={tdStyle}>{user.reason || 'No reason provided'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }
}

// Shared styles (same as your other component)
const thStyle = { padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' };
const tdStyle = { padding: '12px', textAlign: 'left' };
const inputStyle = { padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc', width: '300px' };
const buttonStyle = {
  padding: '10px 16px',
  fontSize: '14px',
  backgroundColor: '#d32f2f',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
};

export default BlacklistTable;